import React, { useState } from 'react';
import { Box, Typography, Paper, Button, Modal } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import {
  AcceptedStatus,
  CancelStatus,
  UncompleteStatus,
  PendingStatus,
} from '../DashboardLable/StatusLables';

import DisableIdentityModal from './IdentifyModal/DisableIdentityModal';
import ConfirmationModal from './IdentifyModal/ConfirmationModal';
import { toast } from 'react-toastify';
import apiClient from '../../../api/apiClient';
import extractApiError from '../../../api/extractApiError';
import { SPACING, RADIUS, ACTION_BLUE } from '../../../theme/theme';

interface IdentityBoxProps {
  status: string;
  publisherId: string;
}

const IdentityBox: React.FC<IdentityBoxProps> = ({ status, publisherId }) => {
  // دریافت شماره موبایل از Redux store
  const { mobile } = useSelector((state: RootState) => state.auth);

  const [isDisableModalOpen, setIsDisableModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [otpTtl, setOtpTtl] = useState(120);
  const [identityData, setIdentityData] = useState<{
    nationalId: string;
    selectedFile: File | null;
  }>({ nationalId: '', selectedFile: null });

  const handleOpenDisableModal = () => {
    setIsDisableModalOpen(true);
  };

  const handleCloseDisableModal = () => {
    setIsDisableModalOpen(false);
  };

  const handleOpenConfirmationModal = async (
    nationalId: string,
    selectedFile: File,
  ) => {
    setIsLoading(true);
    try {
      // ذخیره داده‌های فرم
      setIdentityData({ nationalId, selectedFile });

      // ارسال کد تأیید به شماره کاربر
      const response = await apiClient.post(
        '/v2/publisher/changePublisherTypeSendCode',
      );

      // مهلت اعتبار کد را سرور تعیین می‌کند
      setOtpTtl(response.data?.ttl ?? 120);

      // بستن مودال فرم و باز کردن مودال تأیید کد
      setIsDisableModalOpen(false);
      setIsConfirmationModalOpen(true);
    } catch (error) {
      console.error('Error sending verification code:', error);
      toast.error(extractApiError(error, 'خطا در ارسال کد تأیید'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
  };

  /**
   * Asks the api for a fresh code. The server refuses while a code is still
   * live, so the returned ttl is what the countdown restarts from.
   */
  const handleResendCode = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.post(
        '/v2/publisher/changePublisherTypeSendCode',
      );
      setOtpTtl(response.data?.ttl ?? 120);
      setVerificationCode('');
    } catch (error) {
      // The api refuses while the previous code is still live, and that
      // message is worth showing rather than swallowing.
      console.error('Error resending verification code:', error);
      toast.error(extractApiError(error, 'خطا در ارسال مجدد کد'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async (code: number) => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('publisher_id', publisherId);
      // The company id the modal collected. It used to be captured and then
      // dropped here, so the publisher stayed legal with their personal کد ملی.
      formData.append('national_id', identityData.nationalId);
      if (identityData.selectedFile) {
        formData.append('contract', identityData.selectedFile);
      }
      formData.append('code', code.toString());

      await apiClient.post('/v2/publisher/changePublisherType', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      handleCloseConfirmationModal();
    } catch (error) {
      // A wrong code lands here; without a toast the modal just sat there
      // with no explanation.
      console.error('Error changing identity:', error);
      toast.error(extractApiError(error, 'خطا در تغییر هویت'));
    } finally {
      setIsLoading(false);
    }
  };

  const renderStatus = () => {
    switch (status) {
      case 'PENDING':
        return <PendingStatus />;
      case 'APPROVED':
        return <AcceptedStatus />;
      case 'REJECTED':
        return <CancelStatus />;
      default:
        return <UncompleteStatus />;
    }
  };

  // The theme sets `spacing: 1`, so these numbers are raw pixels — see
  // SPACING/RADIUS in theme/theme.ts. Matches ManageBox.style.ts and
  // ContactInfoBox.style.ts so the three cards in the row agree.
  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        height: '100%',
        padding: `${SPACING.md}px`,
        background: 'white',
        borderRadius: `${RADIUS.md}px`,
        border: '1px solid #E5E5E8',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: `${SPACING.md}px`,
        direction: 'rtl',
        boxSizing: 'border-box',
      }}
    >
      {/* Header */}
      <Typography
        variant="subtitle2"
        color="#A6AAB5"
        fontWeight="500"
        sx={{ wordWrap: 'break-word' }}
      >
        تغییر هویت
      </Typography>

      {/* Status Section */}
      {/* Was a fixed 205px, which fought the card's own height. */}
      <Box
        sx={{
          width: '100%',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1,
            borderRadius: 1,
            padding: '4px 8px',
          }}
        >
          <Typography
            variant="caption"
            color="#60646E"
            sx={{ wordWrap: 'break-word' }}
          >
            وضعیت بررسی
          </Typography>
          {renderStatus()}
        </Box>

        {/* Disable Section */}
        <Box
          sx={{
            borderRadius: 1,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            width: '100%',
          }}
        >
          <Box
            sx={{
              backgroundColor: '#F2F2F5',
              borderRadius: `${RADIUS.sm}px`,
              padding: `${SPACING.sm}px ${SPACING.md}px`,
              width: '100%',
            }}
          >
            <Typography
              variant="body2"
              fontWeight="700"
              color="#25282E"
              sx={{ wordWrap: 'break-word', paddingBottom: '8px' }}
            >
              تغییر هویت به حقوقی
            </Typography>
            <Typography
              variant="caption"
              color="#60646E"
              sx={{ wordWrap: 'break-word', display: 'block', width: '100%' }}
            >
              برای تغییر هویت از حالت حقیقی به حقوقی لازم است که اطلاعات حقوقی
              خود که شامل کد شناسه یازده رقمی به همراه اپلودروزنامه رسمی را برای
              ما ارسال کنیدکه پس ازبررسی تغییراعمال میگردد
            </Typography>
          </Box>

          <Button
            variant="outlined"
            onClick={handleOpenDisableModal}
            sx={{
              borderColor: ACTION_BLUE,
              color: ACTION_BLUE,
              backgroundColor: 'white',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '40px',
              borderRadius: '4px',
              fontSize: 12,
              fontWeight: 500,
              lineHeight: '16px',
              textTransform: 'none',
              width: '120px',
              padding: '8px',
              gap: 1,
              mt: 8,
              alignSelf: 'flex-end',
              '&:hover': {
                backgroundColor: ACTION_BLUE,
                color: 'white',
                borderColor: ACTION_BLUE,
              },
            }}
          >
            <Typography variant="body2" fontWeight="600">
              تغییر هویت
            </Typography>
          </Button>
        </Box>
      </Box>

      {/* Modals */}
      <Modal
        open={isDisableModalOpen}
        onClose={handleCloseDisableModal}
        aria-labelledby="disable-identity-modal"
        aria-describedby="modal-to-disable-identity"
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <DisableIdentityModal
            onClose={handleCloseDisableModal}
            onConfirm={handleOpenConfirmationModal}
            isLoading={isLoading}
          />
        </Box>
      </Modal>

      <Modal
        open={isConfirmationModalOpen}
        onClose={handleCloseConfirmationModal}
        aria-labelledby="confirmation-modal"
        aria-describedby="modal-to-confirm-deactivation"
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <ConfirmationModal
            onClose={handleCloseConfirmationModal}
            onConfirm={handleConfirm}
            verificationCode={verificationCode}
            setVerificationCode={setVerificationCode}
            isLoading={isLoading}
            phoneNumber={mobile}
            ttl={otpTtl}
            onResend={handleResendCode}
          />
        </Box>
      </Modal>
    </Paper>
  );
};

export default IdentityBox;
